import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import {
  Loader2, CheckCircle2, XCircle, Copy, Link2,
  Bell, BellOff, Trophy, Shield, Building2, Save,
} from "lucide-react";
import { SiGoogle } from "react-icons/si";
import { useEffect, useState } from "react";
import { useLocationStore } from "@/lib/store";
import type { Location, Account } from "@shared/schema";

type GoogleStatus = {
  configured: boolean;
  connected: boolean;
  googleEmail: string | null;
};

export default function SettingsPage() {
  const { toast } = useToast();
  const { selectedLocationId } = useLocationStore();

  const { data: googleStatus, isLoading: googleLoading } = useQuery<GoogleStatus>({
    queryKey: ["/api/google/status"],
  });

  const { data: locations = [] } = useQuery<Location[]>({
    queryKey: ["/api/locations"],
  });

  const { data: account } = useQuery<Account>({
    queryKey: ["/api/account"],
  });

  const locationId = selectedLocationId || locations?.[0]?.id;
  const currentLocation = locations?.find((l) => l.id === locationId);

  const [businessName, setBusinessName] = useState("");
  const [businessPhone, setBusinessPhone] = useState("");
  const [businessWebsite, setBusinessWebsite] = useState("");
  const [businessCategory, setBusinessCategory] = useState("");
  const [businessAddress, setBusinessAddress] = useState("");

  useEffect(() => {
    if (currentLocation) {
      setBusinessName(currentLocation.name || "");
      setBusinessPhone(currentLocation.phone || "");
      setBusinessWebsite(currentLocation.website || "");
      setBusinessCategory(currentLocation.category || "");
      setBusinessAddress(currentLocation.address || "");
    }
  }, [currentLocation]);

  const [notifyReviews, setNotifyReviews] = useState(true);
  const [notifyListingChanges, setNotifyListingChanges] = useState(true);
  const [notifyWeeklyReport, setNotifyWeeklyReport] = useState(true);
  const [leaderboardOptIn, setLeaderboardOptIn] = useState(true);

  useEffect(() => {
    if (account) {
      setNotifyReviews(account.notifyReviews ?? true);
      setNotifyListingChanges(account.notifyListingChanges ?? true);
      setNotifyWeeklyReport(account.notifyWeeklyReport ?? true);
      setLeaderboardOptIn(account.leaderboardOptIn ?? true);
    }
  }, [account]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("google_connected") === "true") {
      toast({ title: "Google Connected", description: "Your Google Business Profile account has been linked." });
      queryClient.invalidateQueries({ queryKey: ["/api/google/status"] });
      window.history.replaceState({}, "", "/settings");
    }
    const googleError = params.get("google_error");
    if (googleError) {
      const messages: Record<string, string> = {
        missing_params: "Missing parameters from Google.",
        not_configured: "Google OAuth is not configured yet.",
        token_exchange_failed: "Failed to exchange authorization code.",
        invalid_state: "Security check failed. Please try again.",
        not_authenticated: "You must be logged in to connect Google.",
        server_error: "A server error occurred.",
      };
      toast({
        title: "Google Connection Failed",
        description: messages[googleError] || "An unknown error occurred.",
        variant: "destructive",
      });
      window.history.replaceState({}, "", "/settings");
    }
  }, []);

  const connectMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("GET", "/api/google/auth-url?returnTo=/settings");
      const { authUrl } = await res.json();
      window.location.href = authUrl;
    },
    onError: (err: Error) => {
      toast({
        title: "Cannot connect",
        description: err.message.includes("503")
          ? "Google OAuth is not configured yet. Please add GBP_PERFORMANCE_API_CLIENT_ID and GBP_PERFORMANCE_API_CLIENT_SECRET environment variables."
          : err.message,
        variant: "destructive",
      });
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("DELETE", "/api/google/disconnect");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/google/status"] });
      toast({ title: "Disconnected", description: "Google account has been disconnected." });
    },
  });

  const updateLocationMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("PATCH", `/api/locations/${locationId}`, {
        name: businessName,
        phone: businessPhone,
        website: businessWebsite,
        category: businessCategory,
        address: businessAddress,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/locations"] });
      toast({ title: "Saved", description: "Business information updated." });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to update business info.", variant: "destructive" });
    },
  });

  const updateAccountMutation = useMutation({
    mutationFn: async (data: Record<string, boolean>) => {
      await apiRequest("PATCH", "/api/account", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/account"] });
      toast({ title: "Saved", description: "Preferences updated." });
    },
  });

  const handleCopyShareLink = () => {
    if (currentLocation?.shareToken) {
      const url = `${window.location.origin}/share/${currentLocation.shareToken}`;
      navigator.clipboard.writeText(url);
      toast({ title: "Copied!", description: "Share link copied to clipboard." });
    }
  };

  const shareUrl = currentLocation?.shareToken
    ? `${window.location.origin}/share/${currentLocation.shareToken}`
    : "";

  return (
    <div className="p-6 pb-24 max-w-2xl mx-auto space-y-6 overflow-y-auto h-full" data-testid="page-settings">
      <div>
        <h1 className="text-2xl font-bold" data-testid="text-settings-title">Settings</h1>
        <p className="text-muted-foreground">Manage your account, integrations, and preferences.</p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-base">Business Information</CardTitle>
              <CardDescription>Update your business listing details</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="biz-name">Business Name</Label>
              <Input
                id="biz-name"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                data-testid="input-business-name"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="biz-category">Category</Label>
              <Input
                id="biz-category"
                value={businessCategory}
                onChange={(e) => setBusinessCategory(e.target.value)}
                data-testid="input-business-category"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="biz-phone">Phone</Label>
              <Input
                id="biz-phone"
                value={businessPhone}
                onChange={(e) => setBusinessPhone(e.target.value)}
                data-testid="input-business-phone"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="biz-website">Website</Label>
              <Input
                id="biz-website"
                value={businessWebsite}
                onChange={(e) => setBusinessWebsite(e.target.value)}
                data-testid="input-business-website"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="biz-address">Address</Label>
            <Input
              id="biz-address"
              value={businessAddress}
              onChange={(e) => setBusinessAddress(e.target.value)}
              data-testid="input-business-address"
            />
          </div>
          <Button
            size="sm"
            onClick={() => updateLocationMutation.mutate()}
            disabled={updateLocationMutation.isPending}
            data-testid="button-save-business"
          >
            {updateLocationMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin mr-1" />
            ) : (
              <Save className="w-4 h-4 mr-1" />
            )}
            Save Changes
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
              <SiGoogle className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <CardTitle className="text-base">Google Business Profile</CardTitle>
              <CardDescription>Connect your Google account to manage listings</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {googleLoading ? (
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm text-muted-foreground">Checking connection status...</span>
            </div>
          ) : !googleStatus?.configured ? (
            <div className="space-y-4">
              <Badge variant="outline" className="gap-1">
                <XCircle className="h-3 w-3" />
                Not Configured
              </Badge>
              <div className="p-4 rounded-lg bg-muted/50 text-sm space-y-2">
                <p className="font-medium">Setup Required</p>
                <p className="text-muted-foreground">
                  To connect Google Business Profile, set up Google OAuth credentials in Google Cloud Console,
                  then add GBP_PERFORMANCE_API_CLIENT_ID and GBP_PERFORMANCE_API_CLIENT_SECRET as environment variables.
                </p>
                <div className="mt-3 p-2 bg-background rounded border font-mono text-xs break-all" data-testid="text-redirect-uri">
                  Redirect URI: {window.location.origin}/api/google/callback
                </div>
              </div>
              <Button disabled data-testid="button-connect-google">
                <SiGoogle className="h-4 w-4 mr-2" />
                Connect Google Account
              </Button>
            </div>
          ) : googleStatus?.connected ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Badge variant="default" className="gap-1 bg-green-600">
                  <CheckCircle2 className="h-3 w-3" />
                  Connected
                </Badge>
                {googleStatus.googleEmail && (
                  <span className="text-sm text-muted-foreground" data-testid="text-google-email">
                    {googleStatus.googleEmail}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  onClick={() => (window.location.href = "/link-gbp?returnTo=/settings")}
                  data-testid="button-pick-gbp"
                >
                  Pick a Google Business Profile
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => disconnectMutation.mutate()}
                  disabled={disconnectMutation.isPending}
                  data-testid="button-disconnect-google"
                >
                  {disconnectMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  Disconnect
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <Badge variant="outline" className="gap-1">
                <XCircle className="h-3 w-3" />
                Not Connected
              </Badge>
              <Button
                onClick={() => connectMutation.mutate()}
                disabled={connectMutation.isPending}
                data-testid="button-connect-google"
              >
                {connectMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <SiGoogle className="h-4 w-4 mr-2" />
                )}
                Connect Google Account
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-brand-orange/10 flex items-center justify-center">
              <Link2 className="w-5 h-5 text-brand-orange" />
            </div>
            <div>
              <CardTitle className="text-base">Public Share Link</CardTitle>
              <CardDescription>Share your business performance publicly</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {shareUrl ? (
            <>
              <div className="flex items-center gap-2">
                <Input
                  value={shareUrl}
                  readOnly
                  className="font-mono text-xs"
                  data-testid="input-share-link"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleCopyShareLink}
                  data-testid="button-copy-share-link"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Anyone with this link can see your business rating, coverage percentage, and token balance.
              </p>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">No location selected.</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
              <Trophy className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <CardTitle className="text-base">Leaderboard</CardTitle>
              <CardDescription>Control your visibility on the leaderboard</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Opt in to Leaderboard</p>
              <p className="text-xs text-muted-foreground">
                Show your location on the public leaderboard rankings
              </p>
            </div>
            <Switch
              checked={leaderboardOptIn}
              onCheckedChange={(checked) => {
                setLeaderboardOptIn(checked);
                updateAccountMutation.mutate({ leaderboardOptIn: checked });
              }}
              data-testid="switch-leaderboard"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-brand-blue/10 flex items-center justify-center">
              <Bell className="w-5 h-5 text-brand-blue" />
            </div>
            <div>
              <CardTitle className="text-base">Notification Preferences</CardTitle>
              <CardDescription>Choose which notifications you receive</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">New Reviews</p>
              <p className="text-xs text-muted-foreground">Get notified when a new review is posted</p>
            </div>
            <Switch
              checked={notifyReviews}
              onCheckedChange={(checked) => {
                setNotifyReviews(checked);
                updateAccountMutation.mutate({ notifyReviews: checked });
              }}
              data-testid="switch-notify-reviews"
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Listing Changes</p>
              <p className="text-xs text-muted-foreground">Alert when listing data is modified</p>
            </div>
            <Switch
              checked={notifyListingChanges}
              onCheckedChange={(checked) => {
                setNotifyListingChanges(checked);
                updateAccountMutation.mutate({ notifyListingChanges: checked });
              }}
              data-testid="switch-notify-listing"
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Weekly Report</p>
              <p className="text-xs text-muted-foreground">Receive a weekly summary of your performance</p>
            </div>
            <Switch
              checked={notifyWeeklyReport}
              onCheckedChange={(checked) => {
                setNotifyWeeklyReport(checked);
                updateAccountMutation.mutate({ notifyWeeklyReport: checked });
              }}
              data-testid="switch-notify-weekly"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
